import React, { useMemo } from "react";
import { ArrowDownTrayIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import type { Deal, Document } from "./types/redux";

interface InlineDealsTableProps {
  deals: Deal[];
  documents?: Document[];
  onDealClick: (deal: Deal) => void;
}

/** Column definition for the inline table */
interface ColumnDef {
  key: string;
  header: string;
  className?: string;
  render: (deal: Deal) => React.ReactNode;
}

/** Get the best display name for a deal */
function getDealName(deal: Deal): string {
  return deal.dealName || deal.name || "--";
}

/** Get portfolio company name */
function getCompany(deal: Deal): string {
  if ((deal.sensitivity as string)?.toLowerCase() === "true") return "Sensitive";
  return deal.portfolioCompanyName || "--";
}

/** Get document count from deal, cross-referencing payload-level documents */
function getDocCount(deal: Deal, documents?: Document[]): number {
  const count = deal.documentCount as number | undefined;
  if (typeof count === "number" && count > 0) return count;
  const embeddedDocs = deal.documents as unknown[] | undefined;
  if (Array.isArray(embeddedDocs) && embeddedDocs.length > 0) return embeddedDocs.length;
  if (documents && deal.dealId) {
    const matched = documents.filter((d) => d.dealId === deal.dealId);
    if (matched.length > 0) return matched.length;
  }
  return 0;
}

/** Get description text */
function getDescription(deal: Deal): string {
  return (deal.business as string) || (deal.description as string) || "";
}

/** Truncate text to maxLength */
function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

/** Build TSV string from deals for clipboard / export */
function dealsToTsv(deals: Deal[], documents?: Document[]): string {
  const headers = ["Deal Name", "Company", "Docs", "Description"];
  const rows = deals.map((d) => [
    getDealName(d),
    getCompany(d),
    String(getDocCount(d, documents)),
    getDescription(d),
  ]);
  return [headers.join("\t"), ...rows.map((r) => r.join("\t"))].join("\n");
}

/** Build CSV string from deals for export download */
function dealsToCsv(deals: Deal[], documents?: Document[]): string {
  const headers = ["Deal Name", "Company", "Docs", "Description"];
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const rows = deals.map((d) => [
    getDealName(d),
    getCompany(d),
    String(getDocCount(d, documents)),
    getDescription(d),
  ]);
  return [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
}

export default function InlineDealsTable_Component({ deals, documents, onDealClick }: InlineDealsTableProps): JSX.Element | null {
  const columns = useMemo<ColumnDef[]>(() => [
    {
      key: "dealName",
      header: "Deal Name",
      className: "min-w-[140px]",
      render: (deal) => (
        <span className="text-cvc-blue bg-cvc-linkBg rounded-[3px] px-1 cursor-pointer hover:underline">
          {getDealName(deal)}
        </span>
      ),
    },
    {
      key: "company",
      header: "Company",
      className: "min-w-[120px]",
      render: (deal) => getCompany(deal),
    },
    {
      key: "docs",
      header: "Docs",
      className: "w-[60px] text-center",
      render: (deal) => {
        const count = getDocCount(deal, documents);
        if (count === 0) return <span className="text-gray-400">--</span>;
        return (
          <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-cvc-lb2 text-cvc-blue rounded-full">
            {count}
          </span>
        );
      },
    },
    {
      key: "description",
      header: "Description",
      className: "hidden desktop:table-cell",
      render: (deal) => {
        const desc = getDescription(deal);
        if (!desc) return <span className="text-gray-400">No description</span>;
        return <span title={desc}>{truncate(desc, 120)}</span>;
      },
    },
  ], [documents]);

  if (deals.length === 0) return null;

  const handleExport = () => {
    const csv = dealsToCsv(deals, documents);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "deals.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    const tsv = dealsToTsv(deals, documents);
    try {
      await navigator.clipboard.writeText(tsv);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = tsv;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  return (
    <div className="mt-3 mb-1 border border-cvc-tableBorder rounded-[5px] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-cvc-tableBorder">
          <thead className="bg-cvc-tableHeader">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`py-2 px-3 text-left text-xs font-cvcBody font-normal text-cvc-headerText uppercase tracking-wider ${col.className || ""}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-cvc-tableBorder font-cvcBody">
            {deals.map((deal, idx) => (
              <tr
                key={deal.dealId || deal.id || idx}
                className="hover:bg-cvc-lb2 transition-colors duration-150 cursor-pointer"
                onClick={() => onDealClick(deal)}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`py-2 px-3 text-sm text-cvc-text ${col.key === "description" ? "" : "truncate"} ${col.className || ""}`}
                  >
                    {col.render(deal)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Action row */}
      <div className="flex items-center gap-3 px-3 py-2 bg-cvc-tableHeader border-t border-cvc-tableBorder">
        <button
          onClick={(e) => { e.stopPropagation(); handleExport(); }}
          className="inline-flex items-center gap-1.5 text-xs text-cvc-headerText hover:text-cvc-blue transition-colors"
        >
          <ArrowDownTrayIcon className="w-3.5 h-3.5" />
          Export to Excel
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); void handleCopy(); }}
          className="inline-flex items-center gap-1.5 text-xs text-cvc-headerText hover:text-cvc-blue transition-colors"
        >
          <ClipboardDocumentIcon className="w-3.5 h-3.5" />
          Copy table
        </button>
      </div>
    </div>
  );
}
